"use client"
/* */
import { useReactTable, getFacetedUniqueValues, getPaginationRowModel, getFilteredRowModel, getSortedRowModel, getCoreRowModel } from '@tanstack/react-table';
import invariant from 'invariant';
import { useState, useEffect } from 'react';

class ReactTable {
  list;
  table;
  sortingState;
  updateSortingState;
  constructor(list, onChange, tableOptions = {}) {
    this.list = list;
    const defaultSorting = this.list.sorting.filter(
      (s) => s.defaultEnabled !== false
    );
    const [sortingState, updateSortingState] = useState(
      defaultSorting.map((s) => s.getReactTableColumnSort())
    );
    this.sortingState = sortingState;
    this.updateSortingState = updateSortingState;
    this.table = this.useReactTable(onChange, tableOptions);
  }
  get searchString() {
    return this.table.getState().globalFilter;
  }
  static useNew(list, onChange, tableOptions = {}) {
    return new ReactTable(list, onChange, tableOptions);
  }
  getTableColumn(property) {
    const column = this.table.getColumn(property);
    invariant(!!column, `Column #${property} is not defined`);
    return column;
  }
  useReactTable(onChange, tableOptions = {}) {
    const data = this.list.loader.useData();
    const initialState = {
      pagination: {
        pageSize: this.list.batches.batchSize
      },
      columnFilters: []
    };
    for (const filter of this.list.filters) {
      filter.updateInitialState(initialState);
    }
    this.list.search?.updateInitialState(initialState);
    const table = useReactTable({
      data,
      state: {
        sorting: this.sortingState
      },
      getRowId: this.list.getItemId,
      initialState,
      columns: this.getTableColumnDefs(),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      onSortingChange: (updater) => {
        this.handleSortingStateUpdate(updater);
      },
      globalFilterFn: "auto",
      ...tableOptions
    });
    useEffect(() => {
      if (onChange) {
        onChange(this.list);
      }
    }, [this.list, onChange, table]);
    return table;
  }
  handleSortingStateUpdate(updater) {
    const newSortingState = typeof updater === "function" ? updater(this.sortingState) : updater;
    const additionalHiddenSorting = this.list.sorting.filter(
      (s) => s.defaultEnabled === "hidden" && !newSortingState.some((existing) => existing.id === s.property)
    ).map((s) => s.getReactTableColumnSort());
    this.updateSortingState([...additionalHiddenSorting, ...newSortingState]);
  }
  getTableColumnDefs() {
    const columnDefsMap = /* @__PURE__ */ new Map();
    const getOrCreateColumnDef = (p) => {
      const existing = columnDefsMap.get(p);
      if (existing) {
        return existing;
      }
      const newDef = {
        id: p,
        accessorKey: p,
        enableSorting: false,
        enableColumnFilter: false,
        sortingFn: "alphanumeric"
      };
      columnDefsMap.set(p, newDef);
      return newDef;
    };
    this.list.filters.forEach(
      (f) => f.updateTableColumnDef(getOrCreateColumnDef(f.property))
    );
    this.list.sorting.forEach(
      (s) => s.updateTableColumnDef(getOrCreateColumnDef(s.property))
    );
    this.list.loader.staticDataProperties.forEach((property) => {
      getOrCreateColumnDef(property);
    });
    return Array.from(columnDefsMap.values());
  }
}

export { ReactTable, ReactTable as default };
//# sourceMappingURL=ReactTable.mjs.map
